<template>
  <section id="contact" class="contact bg-decoration">
    <div class="container">
      <div class="contact-content">
        <div class="contact-info">
          <SectionHeader subtitleKey="contact.subtitle" titleKey="contact.title" />
          <p class="contact-description">
            {{ $t('contact.description') }}
          </p>
          <div class="info-items">
            <div class="info-item glass glass-glow" v-for="(item, index) in contactItems" :key="index">
              <div class="info-icon glass-sm">
                <span v-html="item.icon"></span>
              </div>
              <div class="info-text">
                <h4>{{ $t(`contact.info.${item.key}.title`) }}</h4>
                <p>{{ $t(`contact.info.${item.key}.content`) }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="contact-form-wrapper glass glass-glow">
          <form class="contact-form" @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group">
                <label for="name">{{ $t('contact.form.name') }} <span class="required">*</span></label>
                <input type="text" id="name" v-model="form.name" placeholder="请输入您的姓名" required />
              </div>
              <div class="form-group">
                <label for="company">{{ $t('contact.form.company') }} <span class="required">*</span></label>
                <input type="text" id="company" v-model="form.company" placeholder="请输入公司名称" required />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="phone">{{ $t('contact.form.phone') }} <span class="required">*</span></label>
                <input type="tel" id="phone" v-model="form.phone" placeholder="请输入联系电话" required />
              </div>
              <div class="form-group">
                <label for="email">{{ $t('contact.form.email') }}</label>
                <input type="email" id="email" v-model="form.email" placeholder="请输入电子邮箱" />
              </div>
            </div>
            <div class="form-group">
              <label for="service">{{ $t('contact.form.service') }}</label>
              <select id="service" v-model="form.service">
                <option value="">{{ $t('contact.form.servicePlaceholder') }}</option>
                <option value="expertInterview">{{ $t('contact.form.serviceOptions.expertInterview') }}</option>
                <option value="other">{{ $t('contact.form.serviceOptions.other') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="message">{{ $t('contact.form.message') }}</label>
              <textarea id="message" v-model="form.message" rows="5" placeholder="请描述您的咨询需求"></textarea>
            </div>
            <button type="submit" class="submit-btn">{{ $t('contact.form.submit') }}</button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SectionHeader from './common/SectionHeader.vue'
import { iconMap } from '@/assets/icons.js'

const { t } = useI18n()

const form = ref({
  name: '',
  company: '',
  phone: '',
  email: '',
  service: '',
  message: ''
})

const contactItems = ref([
  {
    key: 'address',
    icon: iconMap.address
  },
  {
    key: 'phone',
    icon: iconMap.phone
  },
  {
    key: 'email',
    icon: iconMap.email
  }
])

const handleSubmit = () => {
  alert(t('contact.alert') + '\n\n【演示模式】表单已提交（数据未发送到服务器）')
  form.value = {
    name: '',
    company: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  }
}
</script>

<style scoped>
.contact {
  padding: 120px 0;
  background: linear-gradient(135deg, #e8f0f7 0%, #f1f5f9 50%, #f8fafc 100%);
  position: relative;
}

.contact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 75% 25%, rgba(52, 152, 255, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 25% 75%, rgba(15, 39, 64, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 80px;
  position: relative;
  z-index: 1;
}

.contact-description {
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 36px;
  font-size: 1.05rem;
}

.info-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-item {
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 20px;
  border-radius: 18px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-item:hover {
  transform: translateX(6px);
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(52, 152, 255, 0.15) 0%, rgba(15, 39, 64, 0.08) 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(52, 152, 255, 0.1);
}

.info-icon svg {
  width: 28px;
  height: 28px;
}

.info-text h4 {
  font-size: 1rem;
  color: var(--primary-color);
  margin-bottom: 4px;
  font-weight: 700;
}

.info-text p {
  color: var(--text-light);
  margin: 0;
  font-size: 0.98rem;
}

.contact-form-wrapper {
  padding: 48px;
  border-radius: 28px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.contact-form-wrapper:hover {
  transform: translateY(-4px);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-size: 0.95rem;
  color: var(--text-dark);
  font-weight: 600;
}

.form-group label .required {
  color: #e74c3c;
  margin-left: 4px;
  font-weight: 700;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.6);
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 4px rgba(52, 152, 255, 0.15), 0 4px 12px rgba(52, 152, 255, 0.1);
}

.submit-btn {
  padding: 16px 40px;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: var(--white);
  border: none;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.3);
  align-self: flex-start;
}

.submit-btn:hover {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.4);
}

.submit-btn:active {
  transform: translateY(-1px);
}

@media (max-width: 1024px) {
  .contact-content {
    grid-template-columns: 1fr;
    gap: 50px;
  }
  .contact-info {
    order: -1;
  }
}

@media (max-width: 768px) {
  .contact {
    padding: 80px 0;
  }
  .contact-content {
    gap: 40px;
  }
  .contact-description {
    font-size: 1rem;
    margin-bottom: 30px;
  }
  .info-items {
    gap: 16px;
  }
  .info-item {
    padding: 18px;
  }
  .info-icon {
    width: 52px;
    height: 52px;
  }
  .contact-form-wrapper {
    padding: 32px 24px;
  }
  .form-row {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .submit-btn {
    width: 100%;
    padding: 14px 30px;
    font-size: 1.02rem;
    font-weight: 700;
  }
  .form-group label {
    font-size: 0.92rem;
  }
  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.98rem;
    padding: 12px 16px;
  }
}
</style>
